import React from 'react';
import PropTypes from 'prop-types';
import { HiMinus, HiPlus } from 'react-icons/hi';
import classNames from 'classnames';

const QuantityStepper = ({
    value,
    onChange,
    min = 0,
    max,
    step = 1,
    disabled = false,
    className
}) => {
    const handleDecrease = () => {
        if (disabled) return;
        let newValue = (Number(value) || 0) - step;
        if (newValue < min) newValue = min;
        onChange(newValue);
    };

    const handleIncrease = () => {
        if (disabled) return;
        let newValue = (Number(value) || 0) + step;
        if (max !== undefined && newValue > max) newValue = max;
        onChange(newValue);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val === '') {
            onChange('');
            return;
        }
        
        let numVal = parseInt(val, 10);
        if (isNaN(numVal)) return;

        // Note: We don't strictly enforce min/max on typing to allow intermediate values (e.g. deleting to type new number)
        // But you might want to enforce it onBlur. For now, we'll trust the parent to handle validation or effect.
        // However, usually steppers enforce limits.
        
        onChange(numVal);
    };

    const handleBlur = () => {
        let numVal = parseInt(value, 10);
        if (isNaN(numVal)) {
            onChange(min);
            return;
        }
        if (numVal < min) onChange(min);
        if (max !== undefined && numVal > max) onChange(max);
    };

    return (
        <div className={classNames("flex items-center border border-gray-300 rounded-md overflow-hidden w-fit", className, { 'opacity-50 cursor-not-allowed': disabled })}>
            <button
                type="button"
                onClick={handleDecrease}
                disabled={disabled || (value <= min)}
                className="p-2 hover:bg-gray-100 disabled:hover:bg-transparent transition-colors border-r border-gray-200 text-gray-600"
            >
                <HiMinus />
            </button>
            <input
                type="number"
                value={value}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={disabled}
                className="w-20 text-center focus:outline-none text-gray-700 font-medium py-1"
                min={min}
                max={max}
            />
            <button
                type="button"
                onClick={handleIncrease}
                disabled={disabled || (max !== undefined && value >= max)}
                className="p-2 hover:bg-gray-100 disabled:hover:bg-transparent transition-colors border-l border-gray-200 text-gray-600"
            >
                <HiPlus />
            </button>
        </div>
    );
};

QuantityStepper.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    disabled: PropTypes.bool,
    className: PropTypes.string
};

export default QuantityStepper;
